import type React from 'react';
import Icon from './Icon';
import IconButton from './IconButton';

export type AlertVariant = 'error' | 'success' | 'warning' | 'info';

export interface AlertProps {
  variant: AlertVariant;
  title?: string;
  message: string;
  icon?: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

const variantStyles = {
  error: {
    container: 'bg-danger-light border-danger-border text-danger',
    icon: 'error' as const,
    iconColor: 'danger' as const
  },
  success: {
    container: 'bg-success-light border-success-border text-success',
    icon: 'check' as const,
    iconColor: 'success' as const
  },
  warning: {
    container: 'bg-warning-light border-warning-border text-warning',
    icon: 'warning' as const,
    iconColor: 'warning' as const
  },
  info: {
    container: 'bg-info-light border-info-border text-info',
    icon: 'info' as const,
    iconColor: 'primary' as const
  }
};

export const Alert: React.FC<AlertProps> = ({
  variant,
  title,
  message,
  icon,
  dismissible = false,
  onDismiss,
  className = ''
}) => {
  const styles = variantStyles[variant];
  
  return (
    <div 
      className={`
        p-4 rounded-lg border shadow-sm 
        ${styles.container}
        ${className}
      `}
      role="alert"
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {icon ?? <Icon name={styles.icon} size="md" color={styles.iconColor} />}
        </div>
        
        <div className="ml-3 flex-1">
          {title && (
            <h3 className="text-sm font-medium mb-1">
              {title}
            </h3>
          )}
          <div className="text-sm">
            {message}
          </div>
        </div>
        
        {dismissible && onDismiss && (
          <div className="ml-auto pl-3">
            <IconButton
              onClick={onDismiss}
              variant="ghost"
              size="small"
              title="Dismiss"
              aria-label="Dismiss alert"
              icon={<Icon name="close" size="sm" />}
            />
          </div>
        )}
      </div>
    </div>
  );
};

Alert.displayName = 'Alert';

export default Alert;